import { combineReducers } from 'redux';
import _ from 'lodash';

import {
  createApiReducer,
  chainReducers,
  INITIAL_API_STATE_ITEMS,
} from 'src/api';
import { transformPerson } from 'src/account/redux/account/person/reducers';

import {
  FETCH_FIS_START,
  FETCH_FIS_SUCCESS,
  FETCH_FIS_FAILURE,
  ADD_FI_START,
  ADD_FI_SUCCESS,
  ADD_FI_FAILURE,
  UPDATE_FI_START,
  UPDATE_FI_SUCCESS,
  UPDATE_FI_FAILURE,
  DELETE_FI_START,
  DELETE_FI_SUCCESS,
  DELETE_FI_FAILURE,
  FETCH_FI_SETTINGS_START,
  FETCH_FI_SETTINGS_SUCCESS,
  FETCH_FI_SETTINGS_FAILURE,
  UPDATE_FI_SETTINGS_START,
  UPDATE_FI_SETTINGS_SUCCESS,
  UPDATE_FI_SETTINGS_FAILURE,
  FETCH_FI_PEOPLE_START,
  FETCH_FI_PEOPLE_SUCCESS,
  FETCH_FI_PEOPLE_FAILURE,
  ADD_FI_PERSON_START,
  ADD_FI_PERSON_SUCCESS,
  ADD_FI_PERSON_FAILURE,
  UPDATE_FI_PERSON_START,
  UPDATE_FI_PERSON_SUCCESS,
  UPDATE_FI_PERSON_FAILURE,
  DELETE_FI_PERSON_START,
  DELETE_FI_PERSON_SUCCESS,
  DELETE_FI_PERSON_FAILURE,
  FETCH_FI_DOCUMENT_TYPES_START,
  FETCH_FI_DOCUMENT_TYPES_SUCCESS,
  FETCH_FI_DOCUMENT_TYPES_FAILURE,
  ADD_FI_DOCUMENT_TYPE_START,
  ADD_FI_DOCUMENT_TYPE_SUCCESS,
  ADD_FI_DOCUMENT_TYPE_FAILURE,
  UPDATE_FI_DOCUMENT_TYPE_START,
  UPDATE_FI_DOCUMENT_TYPE_SUCCESS,
  UPDATE_FI_DOCUMENT_TYPE_FAILURE,
  DELETE_FI_DOCUMENT_TYPE_START,
  DELETE_FI_DOCUMENT_TYPE_SUCCESS,
  DELETE_FI_DOCUMENT_TYPE_FAILURE,
  FETCH_FI_CONSENT_TYPES_START,
  FETCH_FI_CONSENT_TYPES_SUCCESS,
  FETCH_FI_CONSENT_TYPES_FAILURE,
  ADD_FI_CONSENT_TYPE_START,
  ADD_FI_CONSENT_TYPE_SUCCESS,
  ADD_FI_CONSENT_TYPE_FAILURE,
  UPDATE_FI_CONSENT_TYPE_START,
  UPDATE_FI_CONSENT_TYPE_SUCCESS,
  UPDATE_FI_CONSENT_TYPE_FAILURE,
  DELETE_FI_CONSENT_TYPE_START,
  DELETE_FI_CONSENT_TYPE_SUCCESS,
  DELETE_FI_CONSENT_TYPE_FAILURE,
} from './actions';

export const transformFI = response => {
  return {
    id: response.id,
    businessName: response.financial_institution_business_name,
    institutionType: _.get(response.financial_institution_type, 'id', null),
    logo: response.financial_institution_logo,
    logoSmall: response.financial_institution_logo_small,
    logoBlue: response.financial_institution_logo_blue,
    logoMail: response.financial_institution_logo_email,
    supportName: response.financial_institution_support_name,
    supportPhone: response.financial_institution_support_phone,
    allowsBanking: response.financial_institution_allows_banking,
    allowsPayments: response.financial_institution_allows_payments,
    allowsLending: response.financial_institution_allows_lending,
    ndaTemplateId: response.financial_institution_nda_template_id,
    personEmailAddress: response.user_email_address,
    personFirstName: response.user_first_name,
    personLastName: response.user_last_name,
    personPrimaryNumber: response.user_primary_number,
    backgroundCheckConsent:
      response.financial_institution_background_check_consent,
  };
};

const transformFIs = response => {
  return { items: response.map(transformFI) };
};

const transformDocumentType = response => {
  return {
    id: response.id,
    name: response.document_type_type,
    description: response.document_type_description,
  };
};

const transformConsentType = response => {
  return {
    id: response.id,
    name: response.consent_type_type,
    longDescription: response.consent_type_long_description,
    shortDescription: response.consent_type_short_description,
    isBackgroundCheck: response.consent_type_is_background_check,
    isSolventBackgroundCheck: response.consent_type_is_solvent_background_check,
  };
};

const fetchFIsReducer = createApiReducer(
  [FETCH_FIS_START, FETCH_FIS_SUCCESS, FETCH_FIS_FAILURE],
  transformFIs,
  error => _.get(error, 'payload.response', 'unknown error')
);

const addFIReducer = createApiReducer(
  [ADD_FI_START, ADD_FI_SUCCESS, ADD_FI_FAILURE],
  (payload, state) => {
    const newFI = transformFI(payload);

    return {
      ...state.data,
      items: [...state.data.items, newFI],
    };
  },
  error => transformFI(_.get(error, 'payload.response', {}))
);

const updateItem = (list, key, value, newItem) => {
  return list.map(item => {
    if (item[key] === value) {
      return newItem;
    }
    return item;
  });
};

const updateFIReducer = createApiReducer(
  [UPDATE_FI_START, UPDATE_FI_SUCCESS, UPDATE_FI_FAILURE],
  (payload, state) => {
    const newFI = transformFI(payload);

    return {
      ...state.data,
      items: updateItem(state.data.items, 'id', newFI.id, newFI),
    };
  },
  error => transformFI(_.get(error, 'payload.response', {}))
);

const deleteFIReducer = createApiReducer(
  [DELETE_FI_START, DELETE_FI_SUCCESS, DELETE_FI_FAILURE],
  (payload, state) => {
    return {
      ...state.data,
      items: state.data.items.filter(fi => fi.id !== payload.id),
    };
  },
  error => transformFI(_.get(error, 'payload.response', {}))
);

export const financialInstitutions = chainReducers(
  INITIAL_API_STATE_ITEMS,
  fetchFIsReducer,
  addFIReducer,
  updateFIReducer,
  deleteFIReducer
);

/* FI Settings */
export const transformFISetting = response => {
  return {
    financial_institution_dba_name: response.financial_institution_dba_name,
    financial_institution_restricted_words:
      response.financial_institution_restricted_words,
    financial_institution_media_check_emails:
      response.financial_institution_media_check_emails,
    financial_institution_tin: response.financial_institution_tin,
    financial_institution_phone_number:
      response.financial_institution_phone_number,
    financial_institution_email_address:
      response.financial_institution_email_address,
    financial_institution_website: response.financial_institution_website,
    financial_institution_registered_state:
      response.financial_institution_registered_state,
    financial_institution_support_name:
      response.financial_institution_support_name,
    financial_institution_support_title:
      response.financial_institution_support_title,
    financial_institution_support_email:
      response.financial_institution_support_email,
    financial_institution_support_phone:
      response.financial_institution_support_phone,
    financial_institution_primary_address:
      response.financial_institution_primary_address,
    financial_institution_primary_address_2:
      response.financial_institution_primary_address_2,
    financial_institution_primary_city:
      response.financial_institution_primary_city,
    financial_institution_primary_state:
      response.financial_institution_primary_state,
    financial_institution_primary_zipcode:
      response.financial_institution_primary_zipcode,
    financial_institution_mailing_address:
      response.financial_institution_mailing_address,
    financial_institution_mailing_address_2:
      response.financial_institution_mailing_address_2,
    financial_institution_mailing_city:
      response.financial_institution_mailing_city,
    financial_institution_mailing_state:
      response.financial_institution_mailing_state,
    financial_institution_mailing_zipcode:
      response.financial_institution_mailing_zipcode,
    financial_institution_sar_contact_office:
      response.financial_institution_sar_contact_office,
    financial_institution_sar_contact_phone:
      response.financial_institution_sar_contact_phone,
    financial_institution_filing_institution_id:
      response.financial_institution_filing_institution_id,
    financial_institution_filing_institution_type:
      response.financial_institution_filing_institution_type,
    financial_institution_filing_institution_identification_type:
      response.financial_institution_filing_institution_identification_type,
    financial_institution_primary_federal_regulator:
      response.financial_institution_primary_federal_regulator,
    financial_institution_invite_email:
      response.financial_institution_invite_email,
    financial_institution_payment_account_invite:
      response.financial_institution_payment_account_invite,
    financial_institution_fi_admin_invite:
      response.financial_institution_fi_admin_invite,
    financial_institution_payment_account_person_invite:
      response.financial_institution_payment_account_person_invite,
    financial_institution_assign_admins_to_accounts:
      response.financial_institution_assign_admins_to_accounts,
  };
};

export const transformFISettings = response => {
  return transformFISetting(response);
};

const getFISettingsReducers = createApiReducer(
  [
    FETCH_FI_SETTINGS_START,
    FETCH_FI_SETTINGS_SUCCESS,
    FETCH_FI_SETTINGS_FAILURE,
  ],
  (payload, state) => {
    const transformedResponse = transformFISettings(payload);
    return {
      ...state.data,
      items: transformedResponse,
    };
  },
  error => _.get(error, 'payload.response', 'unknown error')
);

const updateFISettings = (payload, state) => {
  const newSettings = transformFISetting(payload);

  return {
    ...state.data,
    items: newSettings,
  };
};

const updateFISettingsReducer = createApiReducer(
  [
    UPDATE_FI_SETTINGS_START,
    UPDATE_FI_SETTINGS_SUCCESS,
    UPDATE_FI_SETTINGS_FAILURE,
  ],
  updateFISettings,
  error => transformFISetting(_.get(error, 'payload.response', {}))
);

/* people */

const fetchPeopleReducer = createApiReducer(
  [FETCH_FI_PEOPLE_START, FETCH_FI_PEOPLE_SUCCESS, FETCH_FI_PEOPLE_FAILURE],
  (payload, state) => {
    return {
      ...state.data,
      items: payload.map(transformPerson),
    };
  },
  error => _.get(error, 'payload')
);

const addPersonReducer = createApiReducer(
  [ADD_FI_PERSON_START, ADD_FI_PERSON_SUCCESS, ADD_FI_PERSON_FAILURE],
  (payload, state) => {
    return {
      ...state.data,
      items: [...state.data.items, transformPerson(payload)],
    };
  },
  error => transformPerson(_.get(error, 'payload.response', {}))
);

const updatePersonReducer = createApiReducer(
  [UPDATE_FI_PERSON_START, UPDATE_FI_PERSON_SUCCESS, UPDATE_FI_PERSON_FAILURE],
  (payload, state) => {
    const newPerson = transformPerson(payload);

    return {
      ...state.data,
      items: updateItem(state.data.items, 'id', newPerson.id, newPerson),
    };
  },
  error => transformPerson(_.get(error, 'payload.response', {}))
);

const deletePersonReducer = createApiReducer(
  [DELETE_FI_PERSON_START, DELETE_FI_PERSON_SUCCESS, DELETE_FI_PERSON_FAILURE],
  (payload, state) => {
    return {
      ...state.data,
      items: state.data.items.filter(p => p.id !== payload.id),
    };
  },
  error => transformPerson(_.get(error, 'payload.response', {}))
);

/* documentTypes */

const fetchDocumentTypesReducer = createApiReducer(
  [
    FETCH_FI_DOCUMENT_TYPES_START,
    FETCH_FI_DOCUMENT_TYPES_SUCCESS,
    FETCH_FI_DOCUMENT_TYPES_FAILURE,
  ],
  (payload, state) => {
    return {
      ...state.data,
      items: payload.map(transformDocumentType),
    };
  },
  error => _.get(error, 'payload')
);

const addDocumentTypeReducer = createApiReducer(
  [
    ADD_FI_DOCUMENT_TYPE_START,
    ADD_FI_DOCUMENT_TYPE_SUCCESS,
    ADD_FI_DOCUMENT_TYPE_FAILURE,
  ],
  (payload, state) => {
    return {
      ...state.data,
      items: [...state.data.items, transformDocumentType(payload)],
    };
  },
  error => transformDocumentType(_.get(error, 'payload.response', {}))
);

const updateDocumentTypeReducer = createApiReducer(
  [
    UPDATE_FI_DOCUMENT_TYPE_START,
    UPDATE_FI_DOCUMENT_TYPE_SUCCESS,
    UPDATE_FI_DOCUMENT_TYPE_FAILURE,
  ],
  (payload, state) => {
    const newDocumentType = transformDocumentType(payload);

    return {
      ...state.data,
      items: updateItem(
        state.data.items,
        'id',
        newDocumentType.id,
        newDocumentType
      ),
    };
  },
  error => transformDocumentType(_.get(error, 'payload.response', {}))
);

const deleteDocumentTypeReducer = createApiReducer(
  [
    DELETE_FI_DOCUMENT_TYPE_START,
    DELETE_FI_DOCUMENT_TYPE_SUCCESS,
    DELETE_FI_DOCUMENT_TYPE_FAILURE,
  ],
  (payload, state) => {
    return {
      ...state.data,
      items: state.data.items.filter(p => p.id !== payload.id),
    };
  },
  error => transformDocumentType(_.get(error, 'payload.response', {}))
);

/* consentTypes */

const fetchConsentTypesReducer = createApiReducer(
  [
    FETCH_FI_CONSENT_TYPES_START,
    FETCH_FI_CONSENT_TYPES_SUCCESS,
    FETCH_FI_CONSENT_TYPES_FAILURE,
  ],
  (payload, state) => {
    return {
      ...state.data,
      items: payload.map(transformConsentType),
    };
  },
  error => _.get(error, 'payload')
);

const addConsentTypeReducer = createApiReducer(
  [
    ADD_FI_CONSENT_TYPE_START,
    ADD_FI_CONSENT_TYPE_SUCCESS,
    ADD_FI_CONSENT_TYPE_FAILURE,
  ],
  (payload, state) => {
    return {
      ...state.data,
      items: [...state.data.items, transformConsentType(payload)],
    };
  },
  error => transformConsentType(_.get(error, 'payload.response', {}))
);

const updateConsentTypeReducer = createApiReducer(
  [
    UPDATE_FI_CONSENT_TYPE_START,
    UPDATE_FI_CONSENT_TYPE_SUCCESS,
    UPDATE_FI_CONSENT_TYPE_FAILURE,
  ],
  (payload, state) => {
    const newConsentType = transformConsentType(payload);

    return {
      ...state.data,
      items: updateItem(
        state.data.items,
        'id',
        newConsentType.id,
        newConsentType
      ),
    };
  },
  error => transformConsentType(_.get(error, 'payload.response', {}))
);

const deleteConsentTypeReducer = createApiReducer(
  [
    DELETE_FI_CONSENT_TYPE_START,
    DELETE_FI_CONSENT_TYPE_SUCCESS,
    DELETE_FI_CONSENT_TYPE_FAILURE,
  ],
  (payload, state) => {
    return {
      ...state.data,
      items: state.data.items.filter(p => p.id !== payload.id),
    };
  },
  error => transformConsentType(_.get(error, 'payload.response', {}))
);

export const fiSettings = chainReducers(
  INITIAL_API_STATE_ITEMS,
  getFISettingsReducers,
  updateFISettingsReducer
);

export const people = chainReducers(
  INITIAL_API_STATE_ITEMS,
  fetchPeopleReducer,
  addPersonReducer,
  updatePersonReducer,
  deletePersonReducer
);

export const documentTypes = chainReducers(
  INITIAL_API_STATE_ITEMS,
  fetchDocumentTypesReducer,
  addDocumentTypeReducer,
  updateDocumentTypeReducer,
  deleteDocumentTypeReducer
);

export const consentTypes = chainReducers(
  INITIAL_API_STATE_ITEMS,
  fetchConsentTypesReducer,
  addConsentTypeReducer,
  updateConsentTypeReducer,
  deleteConsentTypeReducer
);

export const reducer = combineReducers({
  financialInstitutions,
  fiSettings,
  people,
  documentTypes,
  consentTypes,
});
